import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { ValidationSummary } from "../components/ui/validated-input";
import {
  Save,
  Settings,
  X,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { companyAPI } from "../services/api";
import { globalErrorHandler, ErrorUtils } from "../lib/errorHandler";
import { useValidation } from "../hooks/useValidation";
import { CommonSchemas } from "../lib/validation";
import { cn } from "../lib/utils";
import { TUNISIA_DEFAULTS } from "../lib/constants";
import { useCompanyInfo } from "../hooks/useCompanyInfo";
import { useCountryConfig } from "../hooks/useCountryConfig";
import CompanyIdentityCard from "../components/company/CompanyIdentityCard";
import CompanyAddressCard from "../components/company/CompanyAddressCard";
import CompanySystemCard from "../components/company/CompanySystemCard";
import CompanyHRCard from "../components/company/CompanyHRCard";
import CompanyMobileCard from "../components/company/CompanyMobileCard";
import CompanySaveBar from "../components/company/CompanySaveBar";

const normalizeDataForForm = (data) => {
  const normalized = {};
  Object.keys(data).forEach((key) => {
    let value = data[key];
    if (value === undefined || value === null) value = "";
    if (typeof value === "number") value = String(value);
    normalized[key] = value;
  });
  return normalized;
};

const formatCapitalSocial = (value, currency = "TND") => {
  if (!value || value === "") return "";
  const numValue = parseFloat(value.toString().replace(/[^\d]/g, ""));
  if (isNaN(numValue)) return "";
  return numValue.toLocaleString(i18n.language, { useGrouping: true, minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/,/g, " ") + ` ${currency}`;
};

const parseCapitalSocial = (formattedValue) => {
  if (!formattedValue) return "";
  return formattedValue.toString().replace(/[^\d]/g, "");
};

const AI_PROVIDERS = [
  { value: "anthropic", label: "Anthropic (Claude)" },
  { value: "openai", label: "OpenAI (GPT)" },
  { value: "openrouter", label: "OpenRouter" },
  { value: "groq", label: "Groq (Llama)" },
];

const CompanyConfiguration = () => {
  const { t } = useTranslation();
  const [originalcompany, setOriginalcompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [capitalSocialDisplay, setCapitalSocialDisplay] = useState("");

  // AI settings state
  const [aiSettings, setAiSettings] = useState({ provider: "anthropic", model_name: "", enabled: false, key_configured: false });
  const [aiKey, setAiKey] = useState(""); // plaintext, only set when user types
  const [aiSaving, setAiSaving] = useState(false);
  const [aiTesting, setAiTesting] = useState(false);
  const companyInfo = useCompanyInfo();
  const formRef = useRef(null);
  const countryCode = companyInfo.company?.country_code || "TN";
  const { config: countryConfig } = useCountryConfig(countryCode);
  const phonePlaceholder = countryConfig?.phone?.placeholder;

  const {
    data: company,
    errors,
    isValid,
    isValidating,
    hasUnsavedChanges,
    isInitialized,
    updateField,
    validateForm,
    resetForm,
    setFormData,
    getFieldProps,
  } = useValidation(
    {
      legal_name: "",
      trade_name: "",
      siret: "",
      siren: "",
      address_line1: "",
      city: "",
      postal_code: "",
      country: TUNISIA_DEFAULTS.country,
      phone: "",
      email: "",
      website: "",
      legal_form: "SARL",
      share_capital: "",
      ape_code: "",
      vat_number: "",
      logo_url: "",
      primary_color: "#3B82F6",
      country_code: "TN",
      default_currency: TUNISIA_DEFAULTS.currency,
      time_zone: TUNISIA_DEFAULTS.timezone,
      date_format: "DD/MM/YYYY",
      time_format: "HH:mm",
      decimal_separator: ",",
      thousand_separator: " ",
      display_currency: TUNISIA_DEFAULTS.currency,
      currency_symbol_position: "after",
      decimal_places: 3,
      email_notifications_enabled: true,
      sms_notifications_enabled: false,
      sender_email: "",
      automatic_backup: true,
      backup_frequency: "daily",
      backup_retention_days: 30,
      session_duration_minutes: 480,
    },
    CommonSchemas.company
  );

  const normalizedData = useMemo(() => {
    const mappedData = {
      legal_name: companyInfo.company?.legal_name || companyInfo.company?.trade_name || "",
      trade_name: companyInfo.company?.trade_name || "",
      siret: companyInfo.company?.siret || "",
      siren: companyInfo.company?.siren || "",
      legal_form: companyInfo.company?.legal_form || "",
      share_capital: companyInfo.company?.share_capital || "",
      ape_code: companyInfo.company?.ape_code || "",
      vat_number: companyInfo.company?.vat_number || "",
      // Country-specific legal identifiers
      matricule_fiscal: companyInfo.company?.matricule_fiscal || "",
      cr_number: companyInfo.company?.cr_number || "",
      ice: companyInfo.company?.ice || "",
      if_number: companyInfo.company?.if_number || "",
      rc_number: companyInfo.company?.rc_number || "",
      cnss_number: companyInfo.company?.cnss_number || "",
      address_line1: companyInfo.company?.address_line1 || "",
      city: companyInfo.company?.city || "",
      postal_code: companyInfo.company?.postal_code || "",
      country: companyInfo.company?.country || TUNISIA_DEFAULTS.country,
      phone: companyInfo.company?.phone || "",
      email: companyInfo.company?.email || "",
      website: companyInfo.company?.website || "",
      primary_color: companyInfo.company?.primary_color || "#FF5722",
      logo_url: companyInfo.company?.logo_url || "",
      country_code: companyInfo.company?.country_code || "TN",
      // WPS (Gulf) employer identifiers
      wps_employer_id: companyInfo.company?.wps_employer_id || "",
      wps_bank_short_name: companyInfo.company?.wps_bank_short_name || "",
      default_currency: companyInfo.company?.default_currency || TUNISIA_DEFAULTS.currency,
      // System settings
      time_zone: companyInfo.system_settings?.time_zone || TUNISIA_DEFAULTS.timezone,
      display_currency: companyInfo.system_settings?.display_currency || companyInfo.company?.default_currency || TUNISIA_DEFAULTS.currency,
      date_format: companyInfo.system_settings?.date_format || "DD/MM/YYYY",
      time_format: companyInfo.system_settings?.time_format || "HH:mm",
      decimal_separator: companyInfo.system_settings?.decimal_separator || ",",
      thousand_separator: companyInfo.system_settings?.thousand_separator || " ",
      currency_symbol_position: companyInfo.system_settings?.currency_symbol_position || "after",
      decimal_places: companyInfo.system_settings?.decimal_places || 3,
      email_notifications_enabled: companyInfo.system_settings?.email_notifications_enabled ?? true,
      sms_notifications_enabled: companyInfo.system_settings?.sms_notifications_enabled ?? false,
      sender_email: companyInfo.system_settings?.sender_email || "",
      automatic_backup: companyInfo.system_settings?.automatic_backup ?? true,
      backup_frequency: companyInfo.system_settings?.backup_frequency || "daily",
      backup_retention_days: companyInfo.system_settings?.backup_retention_days || 30,
      session_duration_minutes: companyInfo.system_settings?.session_duration_minutes || 480,
      // HR settings
      legal_weekly_hours: companyInfo.hr_settings?.legal_weekly_hours || TUNISIA_DEFAULTS.workingHoursPerWeek,
      working_days_per_week: companyInfo.hr_settings?.working_days_per_week || TUNISIA_DEFAULTS.workingDaysPerWeek,
      standard_daily_hours: companyInfo.hr_settings?.standard_daily_hours || TUNISIA_DEFAULTS.workingHoursPerDay,
      annual_leave_days: companyInfo.hr_settings?.annual_leave_days || TUNISIA_DEFAULTS.annualLeaveDays,
      sick_leave_days: companyInfo.hr_settings?.sick_leave_days || TUNISIA_DEFAULTS.sickLeaveDays,
      maternity_leave_days: companyInfo.hr_settings?.maternity_leave_days || TUNISIA_DEFAULTS.maternityLeaveDays,
      paternity_leave_days: companyInfo.hr_settings?.paternity_leave_days || TUNISIA_DEFAULTS.paternityLeaveDays,
    };
    return normalizeDataForForm(mappedData);
  }, [JSON.stringify(companyInfo)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setFormData(normalizedData);
  }, [normalizedData, setFormData, setOriginalcompany]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCapitalSocialDisplay(company.share_capital ? formatCapitalSocial(company.share_capital, company.default_currency) : "");
  }, [company.share_capital, company.default_currency]);

  useEffect(() => {
    companyAPI.getAISettings()
      .then(r => setAiSettings(settings => ({
        ...settings,
        ...(r.data || {}),
        provider: r.data?.provider || settings.provider || "anthropic",
        model_name: r.data?.model_name || "",
        enabled: Boolean(r.data?.enabled),
        key_configured: Boolean(r.data?.key_configured),
      })))
      .catch(() => {});
  }, []);

  const handleAISave = async () => {
    setAiSaving(true);
    try {
      const payload = { provider: aiSettings.provider, model_name: aiSettings.model_name || null, enabled: aiSettings.enabled };
      if (aiKey) payload.api_key = aiKey;
      const r = await companyAPI.updateAISettings(payload);
      setAiSettings(r.data);
      setAiKey("");
      toast.success(t("companyConfig.ai.saved"));
    } catch (e) {
      toast.error(t("companyConfig.ai.saveError"));
    } finally {
      setAiSaving(false);
    }
  };

  const handleAITest = async () => {
    setAiTesting(true);
    try {
      const r = await companyAPI.testAISettings();
      if (r.data.success) toast.success(r.data.message);
      else toast.error(r.data.message);
    } catch (e) {
      toast.error(t("companyConfig.ai.testError"));
    } finally {
      setAiTesting(false);
    }
  };

  const handleCapitalSocialChange = (e) => {
    setCapitalSocialDisplay(e.target.value);
    updateField("share_capital", parseCapitalSocial(e.target.value));
  };

  const handleCapitalSocialBlur = () => {
    if (company.share_capital) setCapitalSocialDisplay(formatCapitalSocial(company.share_capital, company.default_currency));
  };

  const handleSave = async () => {
    const validationResult = validateForm();
    if (!validationResult.isValid) {
      const errorFields = Object.keys(validationResult.errors);
      toast.error(t("companyConfig.toasts.validationErrors"), {
        description: `Corrigez les erreurs dans: ${errorFields.join(", ")}`,
        duration: 8000,
      });
      const element = document.getElementById(errorFields[0]);
      if (element) { element.scrollIntoView({ behavior: "smooth", block: "center" }); element.focus(); }
      return;
    }

    try {
      setLoading(true);
      const cleanData = { ...company };

      const numericFields = [
        "share_capital", "working_days_per_week", "standard_daily_hours",
        "annual_leave_days", "sick_leave_days", "maternity_leave_days",
        "paternity_leave_days", "legal_weekly_hours",
      ];
      numericFields.forEach((field) => {
        if (cleanData[field] === "" || cleanData[field] === undefined) {
          cleanData[field] = null;
        } else if (typeof cleanData[field] === "string") {
          const numValue = parseFloat(cleanData[field]);
          cleanData[field] = isNaN(numValue) ? null : numValue;
        }
      });

      ["siret", "siren", "ape_code", "vat_number", "trade_name"].forEach((field) => {
        if (cleanData[field] === "") cleanData[field] = null;
      });

      const companyPayload = {
        legal_name: cleanData.legal_name, trade_name: cleanData.trade_name,
        siret: cleanData.siret, siren: cleanData.siren, legal_form: cleanData.legal_form,
        share_capital: cleanData.share_capital, ape_code: cleanData.ape_code,
        vat_number: cleanData.vat_number, address_line1: cleanData.address_line1,
        address_line2: cleanData.address_line2, city: cleanData.city,
        postal_code: cleanData.postal_code, country: cleanData.country,
        phone: cleanData.phone, email: cleanData.email, website: cleanData.website,
        logo_url: cleanData.logo_url, primary_color: cleanData.primary_color,
        country_code: cleanData.country_code || "TN",
        default_currency: cleanData.default_currency,
        // WPS (Gulf) employer identifiers
        wps_employer_id: cleanData.wps_employer_id || null,
        wps_bank_short_name: cleanData.wps_bank_short_name || null,
        // Country-specific legal identifiers
        matricule_fiscal: cleanData.matricule_fiscal || null,
        cr_number: cleanData.cr_number || null,
        ice: cleanData.ice || null,
        if_number: cleanData.if_number || null,
        rc_number: cleanData.rc_number || null,
        cnss_number: cleanData.cnss_number || null,
      };

      const systemSettingsPayload = {
        time_zone: cleanData.time_zone, date_format: cleanData.date_format,
        time_format: cleanData.time_format, decimal_separator: cleanData.decimal_separator,
        thousand_separator: cleanData.thousand_separator,
        // Keep display_currency in sync with default_currency
        display_currency: cleanData.default_currency || cleanData.display_currency,
        currency_symbol_position: cleanData.currency_symbol_position, decimal_places: cleanData.decimal_places,
        email_notifications_enabled: cleanData.email_notifications_enabled,
        sms_notifications_enabled: cleanData.sms_notifications_enabled,
        sender_email: cleanData.sender_email, automatic_backup: cleanData.automatic_backup,
        backup_frequency: cleanData.backup_frequency, backup_retention_days: cleanData.backup_retention_days,
        session_duration_minutes: cleanData.session_duration_minutes,
        password_complexity: cleanData.password_complexity,
        mandatory_two_factor_auth: cleanData.mandatory_two_factor_auth,
      };

      const hrPayload = {
        legal_weekly_hours: cleanData.legal_weekly_hours,
        working_days_per_week: cleanData.working_days_per_week,
        standard_daily_hours: cleanData.standard_daily_hours,
        annual_leave_days: cleanData.annual_leave_days,
        sick_leave_days: cleanData.sick_leave_days,
        maternity_leave_days: cleanData.maternity_leave_days,
        paternity_leave_days: cleanData.paternity_leave_days,
      };

      const [companyResp, systemResp, hrResp] = await Promise.all([
        companyAPI.updateCompanyInfo(companyPayload),
        companyAPI.updateSystemSettings(systemSettingsPayload),
        companyAPI.updateHRSettings(hrPayload),
      ]);

      if (companyResp.status === 200 && systemResp.status === 200 && hrResp.status === 200) {
        if (companyInfo.refreshCompanyInfo) await companyInfo.refreshCompanyInfo();
        setFormData({ ...cleanData });
        setOriginalcompany({ ...cleanData });
        setIsEditing(false);
        toast.success(t("companyConfig.toasts.configurationSaved"), {
          description: t("companyConfig.toasts.configurationSavedDesc"),
          duration: 4000,
        });
      }
    } catch (error) {
      const enhancedError = globalErrorHandler.handleError(error, { operation: "save_company_config", component: "companyConfiguration" });
      if (error.response?.status === 422 && error.response.data?.errors) {
        const msgs = Object.entries(error.response.data.errors).slice(0, 3).map(([f, m]) => `• ${f}: ${m}`).join("\n");
        toast.error(t("companyConfig.toasts.serverValidationErrors"), { description: `Veuillez corriger les erreurs suivantes:\n${msgs}`, duration: 8000 });
      } else if (error.response?.status === 403) {
        toast.error(t("companyConfig.toasts.accessDenied"), { description: t("companyConfig.toasts.accessDeniedDesc"), duration: 6000 });
      } else if (ErrorUtils.isNetworkError(enhancedError)) {
        toast.error(t("companyConfig.toasts.connectionError"), { description: t("companyConfig.toasts.saveError"), duration: 8000 });
      } else if (error.response?.status >= 500) {
        toast.error(t("companyConfig.toasts.serverErrorTitle"), { description: t("companyConfig.toasts.serverError"), duration: 8000 });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) setShowCancelDialog(true);
    else cancelEditing();
  };

  const cancelEditing = () => {
    setFormData({ ...originalcompany });
    setIsEditing(false);
    setShowCancelDialog(false);
  };

  const startEditing = () => {
    resetForm(company);
    setOriginalcompany({ ...company });
    setIsEditing(true);
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("companyConfig.toasts.fileTooLarge"), { description: t("companyConfig.toasts.logoSizeLimit"), duration: 5000 });
        return;
      }
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/gif"];
      if (!allowedTypes.includes(file.type)) {
        toast.error(t("companyConfig.toasts.unsupportedFormat"), { description: t("companyConfig.toasts.unsupportedFormatDesc"), duration: 5000 });
        return;
      }

      const uploadToast = toast.loading(t("companyConfig.toasts.uploadingLogo"), { description: "Traitement du fichier..." });
      try {
        const response = await companyAPI.uploadLogo(file);
        if (response.data?.success) {
          updateField("logo_url", response.data.data.file_path);
          toast.dismiss(uploadToast);
          toast.success(t("companyConfig.toasts.logoUploaded"), {
            description: `${file.name} (${(file.size / 1024).toFixed(1)} KB) - Prêt à sauvegarder`,
            duration: 4000,
          });
        } else {
          throw new Error(t("companyConfig.ai.invalidResponse"));
        }
      } catch (error) {
        toast.dismiss(uploadToast);
        let msg = t("companyConfig.toasts.uploadFailed");
        let desc = t("companyConfig.toasts.uploadFailedRetry");
        if (error.response?.status === 413) { msg = t("companyConfig.toasts.fileTooLarge"); desc = t("companyConfig.toasts.reduceImageSize"); }
        else if (error.response?.status === 400) { msg = t("companyConfig.toasts.invalidFormat"); desc = t("companyConfig.toasts.useImageTypes"); }
        else if (error.response?.status === 422) { msg = t("companyConfig.toasts.corruptedFile"); desc = t("companyConfig.toasts.tryAnotherFile"); }
        else if (error.message) { desc = error.message; }
        toast.error(msg, { description: desc, duration: 8000, action: { label: t("companyConfig.actions.newFile"), onClick: () => { event.target.value = ""; event.target.click(); } } });
      }
    } catch (validationError) {
      toast.error(t("companyConfig.toasts.validationError"), { description: t("companyConfig.toasts.checkFileFormat") });
    }
  };

  if (loading && !isInitialized) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="space-y-4 text-center">
          <div className="animate-spin rounded-full h-12 w-12 mx-auto" style={{ border: "3px solid var(--lm-border)", borderTopColor: "#5e6ad2" }} />
          <p className="text-lg font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("companyConfig.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-24 transition-all duration-700 opacity-100 translate-y-0">
      {hasUnsavedChanges && isEditing && (
        <div className="fixed top-20 right-4 z-50 rounded-lg p-4" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid rgba(240,165,0,0.25)", borderLeft: "3px solid #f0a500" }}>
          <div className="flex items-center space-x-3">
            <div className="p-1.5 rounded-lg" style={{ backgroundColor: "rgba(240,165,0,0.12)" }}><AlertTriangle className="h-4 w-4" style={{ color: "#f0a500" }} /></div>
            <div>
              <p className="font-semibold text-sm" style={{ color: "var(--lm-ink)" }}>{t("companyConfig.messages.unsavedChanges")}</p>
              <p className="text-xs mt-0.5" style={{ color: "var(--lm-ink-2)" }}>{t("companyConfig.messages.unsavedWarning")}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 relative z-10">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold" style={{ color: "var(--lm-ink)", letterSpacing: "-0.5px" }}>
            {t("companyConfig.title")}
          </h1>
          <p className="text-sm font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("companyConfig.subtitle")}</p>
        </div>
        <div className="flex items-center space-x-3">
          {!isEditing ? (
            <button
              onClick={startEditing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ backgroundColor: "#5e6ad2", color: "#ffffff", border: "none" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#828fff")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#5e6ad2")}
            >
              <Settings className="h-4 w-4" />{t("companyConfig.actions.edit")}
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ color: hasUnsavedChanges ? "#f0a500" : "var(--lm-ink-2)", backgroundColor: "var(--lm-s1)", border: `1px solid ${hasUnsavedChanges ? "rgba(240,165,0,0.30)" : "var(--lm-border)"}` }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "var(--lm-ink)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = hasUnsavedChanges ? "#f0a500" : "var(--lm-ink-2)"; }}
              >
                <X className="h-4 w-4" />{t("companyConfig.actions.cancel")}
              </button>
              <button
                onClick={handleSave}
                disabled={loading || !isValid}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: "#5e6ad2", color: "#ffffff", border: "none" }}
                onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = "#828fff"; }}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#5e6ad2")}
              >
                {loading ? <RefreshCw className="animate-spin h-4 w-4" /> : <Save className="h-4 w-4" />}
                {t("companyConfig.actions.save")}
                {hasUnsavedChanges && <span className="ml-1 text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>•</span>}
              </button>
            </>
          )}
        </div>
      </div>

      <form ref={formRef} onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="relative z-10">
        <div className={cn("transition-all duration-500", "opacity-100")}>
          {(() => {
            const hasRealErrors = Object.keys(errors).length > 0;
            const shouldShow = isInitialized && !loading && isValid === false && hasRealErrors && isEditing;
            if (hasRealErrors && isValid === true) return null;
            return shouldShow ? <ValidationSummary errors={errors} /> : null;
          })()}
        </div>

        <div className="grid gap-6 sm:gap-8 grid-cols-1 md:grid-cols-2">
          <CompanyIdentityCard
            company={company}
            getFieldProps={getFieldProps}
            updateField={updateField}
            isEditing={isEditing}
            isValidating={isValidating}
            errors={errors}
            capitalSocialDisplay={capitalSocialDisplay}
            handleCapitalSocialChange={handleCapitalSocialChange}
            handleCapitalSocialBlur={handleCapitalSocialBlur}
            handleLogoUpload={handleLogoUpload}
          />
          <CompanyAddressCard
            getFieldProps={getFieldProps}
            isEditing={isEditing}
            isValidating={isValidating}
            phonePlaceholder={phonePlaceholder}
          />
        </div>

        <div className="grid gap-6 sm:gap-8 grid-cols-1 md:grid-cols-2 mt-6 sm:mt-8">
          <CompanySystemCard
            company={company}
            updateField={updateField}
            isEditing={isEditing}
            isValidating={isValidating}
          />
          <CompanyHRCard
            getFieldProps={getFieldProps}
            isEditing={isEditing}
            isValidating={isValidating}
          />
        </div>
      </form>

      {/* Mobile app onboarding QR — independent of main company form, one QR
          per organization (scan once with the Mawered mobile app instead of
          typing the server URL manually). */}
      <div className="mt-6 sm:mt-8">
        <CompanyMobileCard />
      </div>

      {/* AI Configuration Section — independent of main company form */}
      <div className="mt-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <span>🤖</span>
              <span>{t("companyConfig.ai.title")}</span>
              {aiSettings.key_configured && (
                <span className="ml-2 text-xs font-normal text-green-600 bg-green-50 border border-green-200 rounded px-2 py-0.5">{t("companyConfig.ai.configured")}</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!aiSettings.key_configured && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                {t("companyConfig.ai.notConfigured")}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("companyConfig.ai.provider")}</Label>
                <Select value={aiSettings.provider} onValueChange={(v) => setAiSettings(s => ({ ...s, provider: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AI_PROVIDERS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("companyConfig.ai.modelLabel")} <span className="text-muted-foreground">{t("companyConfig.ai.modelOptional")}</span></Label>
                <Input
                  placeholder={t("companyConfig.ai.modelPlaceholder")}
                  value={aiSettings.model_name || ""}
                  onChange={e => setAiSettings(s => ({ ...s, model_name: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("companyConfig.ai.apiKeyLabel")} {aiSettings.key_configured && <span className="text-muted-foreground text-xs">{t("companyConfig.ai.apiKeyConfigured")}</span>}</Label>
              <Input
                type="password"
                placeholder={aiSettings.key_configured ? "••••••••••••••••" : "sk-ant-... ou sk-... ou gsk_..."}
                value={aiKey}
                onChange={e => setAiKey(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={aiSettings.enabled}
                onCheckedChange={v => setAiSettings(s => ({ ...s, enabled: v }))}
                id="ai-enabled"
              />
              <Label htmlFor="ai-enabled">{t("companyConfig.ai.agentsEnabled")}</Label>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleAISave} disabled={aiSaving} size="sm">
                {aiSaving ? "Sauvegarde…" : "Sauvegarder"}
              </Button>
              {aiSettings.key_configured && (
                <Button variant="outline" onClick={handleAITest} disabled={aiTesting} size="sm">
                  {aiTesting ? "Test en cours…" : "Tester la connexion"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <CompanySaveBar
        isEditing={isEditing}
        hasUnsavedChanges={hasUnsavedChanges}
        loading={loading}
        isValid={isValid}
        handleCancel={handleCancel}
        handleSave={handleSave}
        showCancelDialog={showCancelDialog}
        setShowCancelDialog={setShowCancelDialog}
        cancelEditing={cancelEditing}
      />
    </div>
  );
};

export default CompanyConfiguration;
